// The harness asks the console to run one more turn of the model after a run
// (a recipe proposal). The run that raised it is still releasing the chat,
// so the turn waits for the chat to be free, then runs through the console's
// ordinary path. If the chat moved on meanwhile (the user sent something),
// or never frees up, the turn is skipped and the caller is told, so nothing
// is left pending in silence.
export const HARNESS_TURN_WAIT_MS = 15000;
export function scheduleHarnessTurn({ event, getChat, getAgent, run, movedOn = () => false, onSkipped = () => {}, waitMs = HARNESS_TURN_WAIT_MS, now = Date.now, later = (fn, ms) => setTimeout(fn, ms) }) {
    const messages = Array.isArray(event?.messages) ? event.messages : [];
    if (!messages.length) return false;
    const start = now();
    const skip = (why) => { try { onSkipped(why); } catch { /* the caller's failure is its own */ } };
    const attempt = () => {
        const chat = getChat();
        const agent = getAgent();
        if (!chat || !agent) { skip("no chat"); return; }
        if (movedOn()) { skip("moved on"); return; }
        if (chat.busy) { if (now() - start < waitMs) later(attempt, 250); else skip("busy"); return; }
        Promise.resolve(run(agent, messages, event.reason || "harness")).catch(() => {});
    };
    later(attempt, 0);
    return true;
}

// A status row from the harness applied to a chat: the row goes through the
// items reducer, and `lock` holds or releases the composer.
export function applyHarnessNoteToChat(chat, event, applyEvent) {
    const items = applyEvent(chat.items || [], event, 0);
    const harnessTurn = event?.type === "harness_note" && event.lock === true ? (event.text || "harness")
        : event?.type === "harness_note" && event.lock === false ? null
        : chat.harnessTurn ?? null;
    return { ...chat, items, harnessTurn };
}

// The console's side of a harness_continue: remember how many prompts the
// chat had, so a prompt sent meanwhile counts as the chat moving on; mark
// the chat busy for the turn; release the composer when the turn ends or the
// turn is skipped, and tell the harness when it was skipped.
export function runHarnessContinue({ event, getChat, getAgent, updateChat, runTurn, schedule = scheduleHarnessTurn }) {
    const userCount = (getChat()?.items || []).filter((it) => it.kind === "user").length;
    const release = () => updateChat((c) => (c.harnessTurn ? { ...c, harnessTurn: null } : c));
    return schedule({
        event,
        getChat,
        getAgent,
        movedOn: () => ((getChat()?.items || []).filter((it) => it.kind === "user").length > userCount),
        onSkipped: (why) => { release(); try { event.onSkipped?.(why); } catch { /* the harness's failure is its own */ } },
        run: async (agent, messages, reason) => {
            updateChat((c) => ({ ...c, busy: true, error: null }));
            try { await runTurn(agent, messages, reason); } finally { release(); }
        },
    });
}
